import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ContactForm from "../Components/Contact-form";

const createMapEmbedUrl = ({ latitude, longitude }) =>
  `https://maps.google.com/maps?width=100%25&height=600&hl=en&q=${latitude},${longitude}&t=&z=16&ie=UTF8&iwloc=B&output=embed`;

const locationSources = {
  ao: createMapEmbedUrl({
    latitude: -8.815483,
    longitude: 13.230375,
  }),
  za: createMapEmbedUrl({
    latitude: -33.889642,
    longitude: 18.506465,
  }),
};

function Contact() {
  const { t } = useTranslation();
  const contactItems = t("pages.contact.contactItems", { returnObjects: true });
  const locations = useMemo(
    () => {
      const translatedLocations = t("pages.contact.locations.items", {
        returnObjects: true,
      });

      return translatedLocations.map((location) => ({
        ...location,
        src: locationSources[location.id],
      }));
    },
    [t]
  );

  const [activeLocationId, setActiveLocationId] = useState(locations[0]?.id || "ao");
  const activeLocation =
    locations.find((location) => location.id === activeLocationId) || locations[0];

  useEffect(() => {
    if (!activeLocation) {
      return;
    }

    if (!locations.some((location) => location.id === activeLocationId)) {
      setActiveLocationId(locations[0].id);
    }
  }, [activeLocation, activeLocationId, locations]);

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.delay;
            if (delay) {
              entry.target.style.transitionDelay = `${delay}ms`;
            }

            entry.target.classList.add("show");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero__overlay" />
        <div className="main-container contact-hero__content">
          <div className="row justify-content-center text-center">
            <div className="col-xl-8 col-md-9">
              <h1 className="m-0 c-s-w contact-hero__title reveal-on-scroll fade-up" data-delay="100">
                {t("pages.contact.hero.title")}
              </h1>
              <p className="m-0 f-s-20 l-h-30 c-s-w contact-hero__text reveal-on-scroll fade-up" data-delay="150">
                {t("pages.contact.hero.text")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-section">
        <div className="main-container">
          <div className="row g-5 align-items-center">
            <div className="col-md-6">
              <div className="contact-main__inner">
                <span className="contact-main__eyebrow reveal-on-scroll fade-left" data-delay="100">
                  {t("pages.contact.intro.eyebrow")}
                </span>
                <h2 className="m-0 c-s-w contact-main__title reveal-on-scroll fade-left" data-delay="150">
                  {t("pages.contact.intro.title")}
                </h2>
                <p className="m-0 f-s-16 l-h-24 c-s-w contact-main__description reveal-on-scroll fade-left" data-delay="200">
                  {t("pages.contact.intro.text")}
                </p>

                <div className="contact-main__list">
                  {contactItems.map((item, index) => (
                    <article
                      className="contact-main__item reveal-on-scroll fade-left"
                      data-delay={`${(index + 1) * 150}`}
                      key={item.title}
                    >
                      <div className="contact-main__icon">
                        <img
                          src={
                            index === 0
                              ? "/images/contact-call-icon.svg"
                              : index === 1
                                ? "/images/contact-email-icon.svg"
                                : "/images/linkdin-icon.svg"
                          }
                          alt=""
                        />
                      </div>
                      <div>
                        <h3 className="m-0 c-s-w">{item.title}</h3>
                        <p className="m-0 c-s-w contact-main__primary">{item.primary}</p>
                        <p className="m-0 f-s-14 l-h-22 c-s-w contact-main__secondary">
                          {item.secondary}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="contact-form-card reveal-on-scroll fade-right" data-delay="200">
                <ContactForm website="provestCapital" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-locations">
        <div className="main-container">
          <div className="row g-4 align-items-center">
            <div className="col-md-6">
              <span className="contact-locations__eyebrow reveal-on-scroll fade-left" data-delay="100">
                {t("pages.contact.locations.eyebrow")}
              </span>
              <h2 className="m-0 contact-locations__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="150">
                {t("pages.contact.locations.title")}
              </h2>
              <p className="m-0 f-s-16 l-h-24 c-p contact-locations__text reveal-on-scroll fade-left" data-delay="200">
                {t("pages.contact.locations.text")}
              </p>

              <div className="contact-locations__list reveal-on-scroll fade-up" data-delay="250">
                {locations.map((location) => (
                  <button
                    type="button"
                    key={location.id}
                    className="contact-location-card"
                    onClick={() => setActiveLocationId(location.id)}
                  >
                    <div className="contact-location-card__dot">
                      {activeLocation?.id === location.id ? (
                        <div className="contact-location-card__dot_Active"></div>
                      ) : null}
                    </div>

                    <span>
                      <h3 className="f-s-20 l-h-30 m-0">{location.title}</h3>
                      <p className="m-0 f-s-16 l-h-24 location-desc">{location.address}</p>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="col-md-6">
              <div className="contact-map">
                <div className="contact-map__tabs">
                  {locations.map((location) => (
                    <button
                      type="button"
                      key={location.id}
                      className={`contact-map__tab${
                        activeLocation?.id === location.id
                          ? " contact-map__tab--active"
                          : ""
                      }`}
                      onClick={() => setActiveLocationId(location.id)}
                    >
                      {location.tab}
                    </button>
                  ))}
                </div>

                {activeLocation ? (
                  <iframe
                    key={activeLocation.id}
                    title={activeLocation.mapTitle}
                    src={activeLocation.src}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
